import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import type {
  GoogleDriveFile,
  GoogleDriveNodeFlat,
} from '../../api/google';
import type { DriveScope } from '../../api/google/drive/types';
import {
  googleDriveAboutGet,
  googleDriveChildFoldersList,
  googleDriveDrivesCreate,
  googleDriveDrivesList,
  googleDriveFilesCreate,
  googleDriveFilesGet,
  googleDriveFoldersCreate,
  googleDriveNodesList,
  googleDriveRootFoldersList,
} from '../../api/google';
import { ErrorCodes } from '../../api/errorCodes';
import { toAppError } from '../../api/errors';
import { buildGoogleDriveTree, type GoogleDriveTreeNode } from './buildGoogleDriveTree';
import {
  clearGoogleAuth,
  getGoogleAuth,
  isGoogleAuthenticated,
  loginWithGoogle,
} from '../../auth/google';
import { vscodeTheme } from '../../styles/theme';
import { SearchableSelect } from '../shared/SearchableSelect';
import { useToast } from '../shared/Toast';

interface GoogleDriveExplorerProps {
  selectedFileId: string | null;
  onFileOpen: (file: GoogleDriveFile, content: string, revision?: string) => void;
  onFileClear: () => void;
  refreshToken: number;
}

type DriveRootOption =
  | {
      id: string;
      label: string;
      folderId: string;
      folderName: string;
      scope: 'my_drive';
      driveId?: undefined;
      driveName?: undefined;
    }
  | {
      id: string;
      label: string;
      folderId: string;
      folderName: string;
      scope: 'shared_drive';
      driveId: string;
      driveName: string;
    };

type DriveFolder = {
  id: string;
  name: string;
};

const formatBytes = (value?: string) => {
  if (!value) return 'N/A';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = numeric;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatExpiry = (expiresAt?: number) => {
  if (!expiresAt) return 'Unknown';
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return 'Unknown';
  return expires.toLocaleString();
};

const isExcludedMimeType = (mimeType?: string) => {
  if (!mimeType) return false;
  if (mimeType === 'application/vnd.google-apps.shortcut') {
    return true;
  }
  return (
    mimeType.startsWith('application/vnd.google-apps.') &&
    mimeType !== 'application/vnd.google-apps.folder'
  );
};

export function GoogleDriveExplorer({
  selectedFileId,
  onFileOpen,
  onFileClear,
  refreshToken,
}: GoogleDriveExplorerProps) {
  const { showToast } = useToast();
  const [authState, setAuthState] = useState(getGoogleAuth());
  const [about, setAbout] = useState<Awaited<ReturnType<typeof googleDriveAboutGet>> | null>(
    null
  );
  const [loadingRoots, setLoadingRoots] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [myDriveFolders, setMyDriveFolders] = useState<DriveRootOption[]>([]);
  const [sharedDriveRoots, setSharedDriveRoots] = useState<DriveRootOption[]>([]);
  const [childFolders, setChildFolders] = useState<DriveFolder[]>([]);
  const [treeItems, setTreeItems] = useState<GoogleDriveNodeFlat[]>([]);
  const [creatingItem, setCreatingItem] = useState<'file' | 'folder' | null>(null);
  const [creatingChildFolder, setCreatingChildFolder] = useState(false);
  const [creatingSharedDrive, setCreatingSharedDrive] = useState(false);
  const [sharedDriveCreateDisabled, setSharedDriveCreateDisabled] = useState(false);
  const [rootTab, setRootTab] = useState<'my_drive' | 'shared_drive'>('my_drive');
  const [rootFilterText, setRootFilterText] = useState('');
  const [childFilterText, setChildFilterText] = useState('');
  const [selectedRootId, setSelectedRootId] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const isAuthed = isGoogleAuthenticated();

  const isUnauthorizedError = useCallback((err: unknown) => {
    const appError = toAppError(err);
    return (
      appError.status === 401 || appError.error.error_code === ErrorCodes.UNAUTHORIZED
    );
  }, []);

  const handleApiError = useCallback(
    (err: unknown, fallbackMessage: string, toastMessage?: string) => {
      if (isUnauthorizedError(err)) {
        setAuthRequired(true);
        setErrorMessage(null);
        return;
      }
      const message = err instanceof Error ? err.message : fallbackMessage;
      setErrorMessage(message);
      showToast(toastMessage ?? message, 'error');
    },
    [isUnauthorizedError, showToast]
  );

  const rootFolders = useMemo(
    () => [...myDriveFolders, ...sharedDriveRoots],
    [myDriveFolders, sharedDriveRoots]
  );

  const rootItemsForTab = useMemo(
    () => (rootTab === 'my_drive' ? myDriveFolders : sharedDriveRoots),
    [myDriveFolders, rootTab, sharedDriveRoots]
  );

  const selectedRoot = useMemo(
    () => rootFolders.find((folder) => folder.id === selectedRootId) ?? null,
    [rootFolders, selectedRootId]
  );
  const selectedChild = useMemo(
    () => childFolders.find((folder) => folder.id === selectedChildId) ?? null,
    [childFolders, selectedChildId]
  );

  const loadRootFolders = useCallback(async () => {
    if (!isAuthed) {
      setMyDriveFolders([]);
      setSharedDriveRoots([]);
      return;
    }

    try {
      setLoadingRoots(true);
      setErrorMessage(null);
      setAuthRequired(false);
      const [myDriveResponse, drivesResponse] = await Promise.all([
        googleDriveRootFoldersList({ scope: 'my_drive' }),
        googleDriveDrivesList({}),
      ]);

      const myDriveOptions = myDriveResponse.folders.map((folder) => ({
        id: `my-drive:${folder.id}`,
        label: folder.name,
        folderId: folder.id,
        folderName: folder.name,
        scope: 'my_drive' as const,
      }));

      const sharedDriveOptions = drivesResponse.drives.map((drive) => ({
        id: `shared-drive:${drive.id}`,
        label: drive.name,
        folderId: drive.id,
        folderName: drive.name,
        scope: 'shared_drive' as const,
        driveId: drive.id,
        driveName: drive.name,
      }));

      setMyDriveFolders(myDriveOptions);
      setSharedDriveRoots(sharedDriveOptions);
    } catch (err) {
      handleApiError(
        err,
        'Failed to load Google Drive folders',
        'Google Driveのフォルダ取得に失敗しました'
      );
    } finally {
      setLoadingRoots(false);
    }
  }, [handleApiError, isAuthed]);

  const loadChildFolders = useCallback(
    async (rootFolder: DriveRootOption) => {
      if (!isAuthed) {
        setChildFolders([]);
        return;
      }
      try {
        setLoadingChildren(true);
        setErrorMessage(null);
        setAuthRequired(false);
        const response = await googleDriveChildFoldersList(
          rootFolder.scope === 'shared_drive'
            ? {
                scope: 'shared_drive',
                parent_folder_id: rootFolder.folderId,
                shared_drive_id: rootFolder.driveId,
              }
            : {
                scope: 'my_drive',
                parent_folder_id: rootFolder.folderId,
              }
        );
        setChildFolders(response.folders);
      } catch (err) {
        handleApiError(
          err,
          'Failed to load child folders',
          'Google Driveのフォルダ取得に失敗しました'
        );
      } finally {
        setLoadingChildren(false);
      }
    },
    [handleApiError, isAuthed]
  );

  const loadTree = useCallback(
    async (folderId: string, scope: DriveScope, driveId?: string) => {
      if (!isAuthed) {
        setTreeItems([]);
        return;
      }
      try {
        setLoadingTree(true);
        setErrorMessage(null);
        setAuthRequired(false);
        const response = await googleDriveNodesList(
          scope === 'shared_drive'
            ? {
                scope: 'shared_drive',
                root_folder_id: folderId,
                shared_drive_id: driveId!,
              }
            : {
                scope: 'my_drive',
                root_folder_id: folderId,
              }
        );
        setTreeItems(response.items);
      } catch (err) {
        handleApiError(err, 'Failed to load tree', 'Google Driveのツリー取得に失敗しました');
      } finally {
        setLoadingTree(false);
      }
    },
    [handleApiError, isAuthed]
  );

  useEffect(() => {
    if (!isAuthed) {
      setAbout(null);
      return;
    }
    googleDriveAboutGet()
      .then((response) => {
        setAbout(response);
        setAuthRequired(false);
      })
      .catch((err) => {
        handleApiError(err, 'Failed to load Google Drive info');
      });
  }, [handleApiError, isAuthed]);

  useEffect(() => {
    const handleAuthChange = () => {
      setAuthState(getGoogleAuth());
    };

    window.addEventListener('google-auth-change', handleAuthChange);
    return () => {
      window.removeEventListener('google-auth-change', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setChildFolders([]);
      setTreeItems([]);
      setSharedDriveRoots([]);
      setMyDriveFolders([]);
      setSelectedRootId(null);
      setSharedDriveCreateDisabled(false);
      return;
    }
    void loadRootFolders();
  }, [authState, isAuthed, loadRootFolders, refreshToken]);

  useEffect(() => {
    if (rootFolders.length === 0) {
      if (selectedRootId !== null) {
        setSelectedRootId(null);
      }
      return;
    }
    if (!selectedRootId || !rootFolders.some((folder) => folder.id === selectedRootId)) {
      setSelectedRootId(rootFolders[0].id);
    }
  }, [rootFolders, selectedRootId]);

  useEffect(() => {
    if (!selectedRoot) {
      setChildFolders([]);
      setSelectedChildId(null);
      setTreeItems([]);
      setExpandedFolders(new Set());
      return;
    }
    setSelectedChildId(null);
    setChildFilterText('');
    setExpandedFolders(new Set());
    onFileClear();
    void loadChildFolders(selectedRoot);
  }, [loadChildFolders, onFileClear, selectedRoot]);

  useEffect(() => {
    if (!selectedRoot) {
      setTreeItems([]);
      return;
    }
    const targetFolderId = selectedChild?.id ?? selectedRoot.folderId;
    onFileClear();
    void loadTree(targetFolderId, selectedRoot.scope, selectedRoot.driveId);
  }, [loadTree, onFileClear, selectedChild, selectedRoot]);

  const handleConnect = () => {
    try {
      setAuthRequired(false);
      loginWithGoogle(`${window.location.pathname}?tab=google-drive${window.location.hash}`);
    } catch (err) {
      handleApiError(err, 'Google認証を開始できませんでした');
    }
  };

  const handleDisconnect = () => {
    clearGoogleAuth();
    setAbout(null);
    setErrorMessage(null);
    setAuthRequired(false);
    setMyDriveFolders([]);
    setSharedDriveRoots([]);
    setSharedDriveCreateDisabled(false);
    setChildFolders([]);
    setTreeItems([]);
    setSelectedRootId(null);
    setSelectedChildId(null);
    setRootFilterText('');
    setChildFilterText('');
    onFileClear();
  };

  const handleOpenFile = async (file: { id: string }) => {
    if (loadingFile) {
      return;
    }
    if (!selectedRoot) {
      return;
    }
    try {
      setLoadingFile(true);
      setErrorMessage(null);
      setAuthRequired(false);
      const response = await googleDriveFilesGet(
        selectedRoot.scope === 'shared_drive'
          ? {
              scope: 'shared_drive',
              shared_drive_id: selectedRoot.driveId,
              file_id: file.id,
              format: 'text',
            }
          : {
              scope: 'my_drive',
              file_id: file.id,
              format: 'text',
            }
      );
      if (response.content == null) {
        showToast('This file type is not supported for editor.', 'error');
        return;
      }
      let contentText = '';
      if (typeof response.content === 'string') {
        contentText = response.content;
      } else {
        try {
          contentText = JSON.stringify(response.content, null, 2);
        } catch (stringifyError) {
          const message =
            stringifyError instanceof Error
              ? stringifyError.message
              : 'Failed to parse file content';
          setErrorMessage(message);
          showToast(message, 'error');
          return;
        }
      }
      onFileOpen(response.metadata, contentText, response.metadata.headRevisionId);
    } catch (err) {
      handleApiError(err, 'Failed to open file');
    } finally {
      setLoadingFile(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!selectedRoot) {
      return;
    }
    const name = prompt('New folder name');
    if (!name) {
      return;
    }
    try {
      setCreatingItem('folder');
      setErrorMessage(null);
      setAuthRequired(false);
      const targetFolderId = selectedChild?.id ?? selectedRoot.folderId;
      await googleDriveFoldersCreate(
        selectedRoot.scope === 'shared_drive'
          ? {
              scope: 'shared_drive',
              shared_drive_id: selectedRoot.driveId,
              parent_folder_id: targetFolderId,
              name,
            }
          : {
              scope: 'my_drive',
              parent_folder_id: targetFolderId,
              name,
            }
      );
      if (!selectedChild) {
        await loadChildFolders(selectedRoot);
      }
      await loadTree(targetFolderId, selectedRoot.scope, selectedRoot.driveId);
      showToast('フォルダを作成しました');
    } catch (err) {
      handleApiError(err, 'フォルダの作成に失敗しました');
    } finally {
      setCreatingItem(null);
    }
  };

  const handleCreateFile = async () => {
    if (!selectedRoot) {
      return;
    }
    const name = prompt('New file name');
    if (!name) {
      return;
    }
    try {
      setCreatingItem('file');
      setErrorMessage(null);
      setAuthRequired(false);
      const targetFolderId = selectedChild?.id ?? selectedRoot.folderId;
      const created = await googleDriveFilesCreate(
        selectedRoot.scope === 'shared_drive'
          ? {
              scope: 'shared_drive',
              shared_drive_id: selectedRoot.driveId,
              parent_folder_id: targetFolderId,
              name,
              mime_type: 'text/plain',
              content: '',
            }
          : {
              scope: 'my_drive',
              parent_folder_id: targetFolderId,
              name,
              mime_type: 'text/plain',
              content: '',
            }
      );
      await loadTree(targetFolderId, selectedRoot.scope, selectedRoot.driveId);
      await handleOpenFile(created);
      showToast('ファイルを作成しました');
    } catch (err) {
      handleApiError(err, 'ファイルの作成に失敗しました');
    } finally {
      setCreatingItem(null);
    }
  };

  const handleCreateSharedDrive = async () => {
    const name = rootFilterText.trim();
    if (!name || creatingSharedDrive || sharedDriveCreateDisabled) {
      return;
    }
    try {
      setCreatingSharedDrive(true);
      setErrorMessage(null);
      setAuthRequired(false);
      const created = await googleDriveDrivesCreate({ name });
      const newOption: DriveRootOption = {
        id: `shared-drive:${created.id}`,
        label: created.name,
        folderId: created.id,
        folderName: created.name,
        scope: 'shared_drive',
        driveId: created.id,
        driveName: created.name,
      };
      setSharedDriveRoots((prev) => [...prev, newOption]);
      setSelectedRootId(newOption.id);
      setRootFilterText('');
      showToast('共有ドライブを作成しました');
    } catch (err) {
      if (!isUnauthorizedError(err)) {
        setSharedDriveCreateDisabled(true);
      }
      handleApiError(err, '共有ドライブの作成に失敗しました');
    } finally {
      setCreatingSharedDrive(false);
    }
  };

  const handleCreateChildFolder = async () => {
    if (!selectedRoot) {
      return;
    }
    const name = childFilterText.trim();
    if (!name || creatingChildFolder) {
      return;
    }
    try {
      setCreatingChildFolder(true);
      setErrorMessage(null);
      setAuthRequired(false);
      const parentFolderId = selectedRoot.folderId;
      const created = await googleDriveFoldersCreate(
        selectedRoot.scope === 'shared_drive'
          ? {
              scope: 'shared_drive',
              shared_drive_id: selectedRoot.driveId,
              parent_folder_id: parentFolderId,
              name,
            }
          : {
              scope: 'my_drive',
              parent_folder_id: parentFolderId,
              name,
            }
      );
      setChildFolders((prev) => [...prev, { id: created.id, name: created.name }]);
      setSelectedChildId(created.id);
      setChildFilterText('');
      await loadTree(created.id, selectedRoot.scope, selectedRoot.driveId);
      showToast('フォルダを作成しました');
    } catch (err) {
      handleApiError(err, 'フォルダの作成に失敗しました');
    } finally {
      setCreatingChildFolder(false);
    }
  };

  const tree = useMemo(() => {
    if (!selectedRoot) {
      return [];
    }
    const rootId = selectedChild?.id ?? selectedRoot.folderId;
    const filteredItems = treeItems.filter(
      (item) =>
        item.id !== rootId &&
        item.parent_id !== null &&
        !item.trashed &&
        !isExcludedMimeType(item.mime_type)
    );
    return buildGoogleDriveTree(filteredItems);
  }, [selectedChild?.id, selectedRoot, treeItems]);

  const canCreateSharedDrive =
    rootTab === 'shared_drive' &&
    rootFilterText.trim().length > 0 &&
    !sharedDriveCreateDisabled;
  const canCreateChildFolder = Boolean(selectedRoot) && childFilterText.trim().length > 0;

  useEffect(() => {
    const nextExpanded = new Set<string>();
    const walk = (nodes: GoogleDriveTreeNode[]) => {
      for (const node of nodes) {
        if (node.is_folder) {
          nextExpanded.add(node.id);
          if (node.children) {
            walk(node.children);
          }
        }
      }
    };
    walk(tree);
    setExpandedFolders(nextExpanded);
  }, [tree]);

  const TreeItem = ({ node, depth = 0 }: { node: GoogleDriveTreeNode; depth?: number }) => {
    const isExpanded = expandedFolders.has(node.id);
    const indent = depth * 16;
    const isSelected = !node.is_folder && selectedFileId === node.id;
    const backgroundColor = isSelected ? vscodeTheme.colors.selectedBackground : 'transparent';

    const toggleFolder = () => {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        if (next.has(node.id)) {
          next.delete(node.id);
        } else {
          next.add(node.id);
        }
        return next;
      });
    };

    const handleItemClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (node.is_folder) {
        toggleFolder();
      } else {
        const item = treeItems.find((file) => file.id === node.id);
        if (item) {
          void handleOpenFile(item);
        }
      }
    };

    return (
      <>
        <div
          onClick={handleItemClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            paddingLeft: `${indent + 8}px`,
            cursor: 'pointer',
            color: vscodeTheme.colors.textPrimary,
            fontSize: vscodeTheme.typography.fontSize,
            fontFamily: vscodeTheme.typography.fontFamily,
            backgroundColor,
            transition: 'background-color 0.1s',
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = vscodeTheme.colors.hoverBackground;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {node.is_folder && (
            <span style={{ marginRight: '4px', fontSize: '10px' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          <span style={{ marginRight: '6px' }}>{node.is_folder ? '📁' : '📄'}</span>
          <span style={{ flex: 1 }}>{node.name}</span>
        </div>
        {node.is_folder && isExpanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeItem key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${vscodeTheme.colors.border}`,
          backgroundColor: vscodeTheme.colors.headerBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            color: vscodeTheme.colors.textSecondary,
            fontFamily: vscodeTheme.typography.fontFamily,
          }}
        >
          EXPLORER
        </span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={() => void handleCreateFile()}
            disabled={!isAuthed || creatingItem !== null}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: !isAuthed || creatingItem !== null ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="New file"
          >
            📄+
          </button>
          <button
            type="button"
            onClick={() => void handleCreateFolder()}
            disabled={!isAuthed || creatingItem !== null}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: !isAuthed || creatingItem !== null ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="New folder"
          >
            📁+
          </button>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '12px',
            borderBottom: `1px solid ${vscodeTheme.colors.border}`,
            backgroundColor: vscodeTheme.colors.sidebarBackground,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <SearchableSelect
            label="ROOT"
            items={rootItemsForTab.map((folder) => ({
              id: folder.id,
              label: folder.label,
            }))}
            selectedId={selectedRootId}
            onChange={(id) => setSelectedRootId(id)}
            placeholder={selectedRoot?.folderName ?? 'Filter...'}
            filterText={rootFilterText}
            onFilterTextChange={setRootFilterText}
            emptyText={
              loadingRoots
                ? 'Loading...'
                : rootTab === 'shared_drive'
                ? 'No shared drives found'
                : 'No folders found'
            }
            renderHeader={() => (
              <div
                style={{
                  display: 'flex',
                  gap: '6px',
                  padding: '6px',
                  borderBottom: `1px solid ${vscodeTheme.colors.border}`,
                  backgroundColor: vscodeTheme.colors.headerBackground,
                }}
              >
                <button
                  type="button"
                  onClick={() => setRootTab('shared_drive')}
                  style={{
                    flex: 1,
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    backgroundColor:
                      rootTab === 'shared_drive'
                        ? vscodeTheme.colors.activeBackground
                        : 'transparent',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '11px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                    cursor: 'pointer',
                  }}
                >
                  共有ドライブ
                </button>
                <button
                  type="button"
                  onClick={() => setRootTab('my_drive')}
                  style={{
                    flex: 1,
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    backgroundColor:
                      rootTab === 'my_drive'
                        ? vscodeTheme.colors.activeBackground
                        : 'transparent',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '11px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                    cursor: 'pointer',
                  }}
                >
                  マイドライブ
                </button>
              </div>
            )}
            renderFooter={
              rootTab === 'shared_drive' &&
              (sharedDriveCreateDisabled || rootFilterText.trim().length > 0)
                ? () => {
                    if (sharedDriveCreateDisabled) {
                      return (
                        <div
                          style={{
                            padding: '6px 8px',
                            borderTop: `1px solid ${vscodeTheme.colors.border}`,
                            color: vscodeTheme.colors.textSecondary,
                            fontSize: vscodeTheme.typography.fontSize,
                            fontFamily: vscodeTheme.typography.fontFamily,
                          }}
                        >
                          作成できません
                        </div>
                      );
                    }
                    if (!canCreateSharedDrive) {
                      return null;
                    }
                    const trimmed = rootFilterText.trim();
                    return (
                      <div
                        onClick={creatingSharedDrive ? undefined : handleCreateSharedDrive}
                        style={{
                          padding: '6px 8px',
                          cursor: creatingSharedDrive ? 'not-allowed' : 'pointer',
                          borderTop: `1px solid ${vscodeTheme.colors.border}`,
                          color: vscodeTheme.colors.textPrimary,
                          fontSize: vscodeTheme.typography.fontSize,
                          fontFamily: vscodeTheme.typography.fontFamily,
                          backgroundColor: creatingSharedDrive
                            ? vscodeTheme.colors.editorBackground
                            : 'transparent',
                        }}
                        onMouseEnter={(event) => {
                          if (!creatingSharedDrive) {
                            event.currentTarget.style.backgroundColor =
                              vscodeTheme.colors.hoverBackground;
                          }
                        }}
                        onMouseLeave={(event) => {
                          if (!creatingSharedDrive) {
                            event.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {creatingSharedDrive
                          ? `Creating "${trimmed}"...`
                          : `+ Create shared drive "${trimmed}"`}
                      </div>
                    );
                  }
                : undefined
            }
          />
          <SearchableSelect
            label="SUB FOLDER"
            items={childFolders.map((folder) => ({
              id: folder.id,
              label: folder.name,
            }))}
            selectedId={selectedChildId}
            onChange={(id) => setSelectedChildId(id)}
            placeholder={selectedChild?.name ?? 'Filter...'}
            filterText={childFilterText}
            onFilterTextChange={setChildFilterText}
            emptyText={
              loadingChildren
                ? 'Loading...'
                : selectedRoot
                ? 'No folders found'
                : 'Select a root folder first'
            }
            renderFooter={
              selectedRoot && childFilterText.trim().length > 0
                ? () => {
                    if (!canCreateChildFolder) {
                      return null;
                    }
                    const trimmed = childFilterText.trim();
                    return (
                      <div
                        onClick={creatingChildFolder ? undefined : handleCreateChildFolder}
                        style={{
                          padding: '6px 8px',
                          cursor: creatingChildFolder ? 'not-allowed' : 'pointer',
                          borderTop: `1px solid ${vscodeTheme.colors.border}`,
                          color: vscodeTheme.colors.textPrimary,
                          fontSize: vscodeTheme.typography.fontSize,
                          fontFamily: vscodeTheme.typography.fontFamily,
                          backgroundColor: creatingChildFolder
                            ? vscodeTheme.colors.editorBackground
                            : 'transparent',
                        }}
                        onMouseEnter={(event) => {
                          if (!creatingChildFolder) {
                            event.currentTarget.style.backgroundColor =
                              vscodeTheme.colors.hoverBackground;
                          }
                        }}
                        onMouseLeave={(event) => {
                          if (!creatingChildFolder) {
                            event.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {creatingChildFolder
                          ? `Creating "${trimmed}"...`
                          : `Create folder: "${trimmed}"`}
                      </div>
                    );
                  }
                : undefined
            }
          />
        </div>

        <div
          style={{
            padding: '12px',
            borderBottom: `1px solid ${vscodeTheme.colors.border}`,
            backgroundColor: vscodeTheme.colors.headerBackground,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600 }}>Google Drive</div>
          {!isAuthed ? (
            <>
              <div style={{ fontSize: '12px', color: vscodeTheme.colors.textSecondary }}>
                未ログインです。
              </div>
              <button
                type="button"
                onClick={handleConnect}
                style={{
                  padding: '6px 10px',
                  backgroundColor: vscodeTheme.colors.activeBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  cursor: 'pointer',
                  fontSize: '12px',
                  borderRadius: '6px',
                  fontFamily: vscodeTheme.typography.fontFamily,
                }}
              >
                Connect Google
              </button>
            </>
          ) : authRequired ? (
            <>
              <div style={{ fontSize: '12px', color: vscodeTheme.colors.textSecondary }}>
                Googleログインが必要です。
              </div>
              <button
                type="button"
                onClick={handleConnect}
                style={{
                  padding: '6px 10px',
                  backgroundColor: vscodeTheme.colors.activeBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  cursor: 'pointer',
                  fontSize: '12px',
                  borderRadius: '6px',
                  fontFamily: vscodeTheme.typography.fontFamily,
                }}
              >
                Login with Google
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: '12px', color: vscodeTheme.colors.textSecondary }}>
                {about?.user?.emailAddress ?? 'Connected'}
              </div>
              <div style={{ fontSize: '11px', color: vscodeTheme.colors.textSecondary }}>
                Token expiry: {formatExpiry(authState?.expiresAt)}
              </div>
              <div style={{ fontSize: '11px', color: vscodeTheme.colors.textSecondary }}>
                Drive usage: {formatBytes(about?.storageQuota?.usage)} /{' '}
                {formatBytes(about?.storageQuota?.limit)}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => void loadRootFolders()}
                  disabled={loadingRoots}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: vscodeTheme.colors.buttonBackground,
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    color: vscodeTheme.colors.textPrimary,
                    cursor: loadingRoots ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    borderRadius: '6px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                  }}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: vscodeTheme.colors.buttonBackground,
                    border: `1px solid ${vscodeTheme.colors.border}`,
                    color: vscodeTheme.colors.textPrimary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    borderRadius: '6px',
                    fontFamily: vscodeTheme.typography.fontFamily,
                  }}
                >
                  Disconnect
                </button>
              </div>
            </>
          )}
          {errorMessage && !authRequired && (
            <div style={{ color: vscodeTheme.colors.error, fontSize: '11px' }}>
              {errorMessage}
            </div>
          )}
        </div>

        {loadingTree && (
          <div
            style={{
              fontSize: '12px',
              color: vscodeTheme.colors.textSecondary,
              padding: '8px 12px',
            }}
          >
            Loading...
          </div>
        )}
        {loadingFile && (
          <div
            style={{
              fontSize: '12px',
              color: vscodeTheme.colors.textSecondary,
              padding: '0 12px',
            }}
          >
            Opening file...
          </div>
        )}
        {!loadingTree && tree.length === 0 && isAuthed && selectedRoot && (
          <div
            style={{
              fontSize: '12px',
              color: vscodeTheme.colors.textSecondary,
              padding: '8px 12px',
            }}
          >
            ファイルが見つかりません。
          </div>
        )}
        {!isAuthed && (
          <div
            style={{
              fontSize: '12px',
              color: vscodeTheme.colors.textSecondary,
              padding: '8px 12px',
            }}
          >
            接続するとファイル一覧が表示されます。
          </div>
        )}
        <div>
          {tree.map((node) => (
            <TreeItem key={node.id} node={node} />
          ))}
        </div>
      </div>
    </div>
  );
}
